import { parseTimestamp } from '../lib/traderTime';
import { useTraderTime } from '../contexts/TraderTimeContext';
import { formatCardNumber } from '../lib/utils';
import { ThemeToggle } from '../components/ThemeToggle';
import { bankDisplayName } from '../lib/kgsBanks';
import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL, paymentsAPI, Appeal, AppealStatus, AppealResolution, PaymentTransactionResponse, traderAuthAPI, TraderAppealReason } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Pagination } from '../components/ui/pagination';
import { Loader2, ExternalLink, AlertCircle, Clock, MessageSquare, Calendar, RefreshCw, User, LogOut, Timer, Upload, Send, CreditCard, Phone, Building, CheckCircle, X, Menu } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Sidebar } from '../components/Sidebar';
import { TraderAppealResponse } from '../components/TraderAppealResponse';
import { useNavigate } from 'react-router-dom';

const QUICK_REASONS: { code: TraderAppealReason; label: string }[] = [
    { code: 'requisite_blocked', label: 'Реквизит в блоке' },
    { code: 'invalid_receipt', label: 'Некорректный чек, нужен другой' },
    { code: 'client_statement_required', label: 'Требуется выписка клиента' },
];

const AppealTimer: React.FC<{ createdAt: string; lifetimeMinutes: number; isExpired?: boolean }> = ({
    createdAt,
    lifetimeMinutes,
    isExpired
}) => {
    const [now, setNow] = useState(new Date());

    useEffect(() => {
        const interval = setInterval(() => setNow(new Date()), 60000);
        return () => clearInterval(interval);
    }, []);

    const createdDate = parseTimestamp(createdAt);
    const elapsedMs = now.getTime() - createdDate.getTime();
    const elapsedMinutes = Math.floor(elapsedMs / 60000);
    const remainingMinutes = lifetimeMinutes - elapsedMinutes;

    const formatTime = (minutes: number) => {
        if (minutes <= 0) return 'Истёк';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0) {
            return `${hours}ч ${mins}м`;
        }
        return `${mins}м`;
    };

    let colorClass = 'text-green-600 dark:text-green-300 bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800';
    if (remainingMinutes <= 0 || isExpired) {
        colorClass = 'text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800 font-bold';
    } else if (remainingMinutes <= 30) {
        colorClass = 'text-orange-600 dark:text-orange-300 bg-orange-50 dark:bg-orange-950 border-orange-200 dark:border-orange-800';
    } else if (remainingMinutes <= 60) {
        colorClass = 'text-yellow-600 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800';
    }

    return (
        <div className={`flex items-center gap-1 px-2 py-1 rounded border text-xs ${colorClass}`}>
            <Timer className="h-3 w-3" />
            <span>{isExpired ? 'Истёк' : formatTime(remainingMinutes)}</span>
        </div>
    );
};

export const TraderAppealsPage: React.FC = () => {
    const { formatDate, label, offsetMinutes } = useTraderTime();
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const [appeals, setAppeals] = useState<Appeal[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const requestVersion = useRef(0);
    const latestOffset = useRef(offsetMinutes);
    latestOffset.current = offsetMinutes;
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    // Modal state for viewing appeal details
    const [selectedAppeal, setSelectedAppeal] = useState<Appeal | null>(null);
    const [selectedTransaction, setSelectedTransaction] = useState<PaymentTransactionResponse | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [receiptUrl, setReceiptUrl] = useState<string | null>(null);

    // Respond modal state
    const [isRespondModalOpen, setIsRespondModalOpen] = useState(false);
    const [respondDescription, setRespondDescription] = useState('');
    const [respondFiles, setRespondFiles] = useState<File[]>([]);
    const [otherResponseOpen, setOtherResponseOpen] = useState(false);
    const [responseMode, setResponseMode] = useState<'evidence' | 'recalculation'>('evidence');
    const [requestedAmount, setRequestedAmount] = useState('');
    const [recalculationDescription, setRecalculationDescription] = useState('');
    const [recalculationFiles, setRecalculationFiles] = useState<File[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submittingResponse = useRef(false);
    const quickResponseAvailable = selectedAppeal?.transaction_type === 'deposit' && selectedAppeal.quick_response_available === true;
    const showResponseForm = !quickResponseAvailable || otherResponseOpen;
    const isRecalculation = quickResponseAvailable && responseMode === 'recalculation';
    const activeDescription = isRecalculation ? recalculationDescription : respondDescription;
    const activeFiles = isRecalculation ? recalculationFiles : respondFiles;
    const setActiveDescription = isRecalculation ? setRecalculationDescription : setRespondDescription;
    const setActiveFiles = isRecalculation ? setRecalculationFiles : setRespondFiles;
    const normalizedAmount = requestedAmount.trim().replace(',', '.');
    const validRequestedAmount = /^\d{1,14}(\.\d{1,6})?$/.test(normalizedAmount)
        && Number(normalizedAmount) > 0 && Number(normalizedAmount) !== selectedAppeal?.amount;
    const responseReady = showResponseForm && (isRecalculation ? validRequestedAmount : respondFiles.length > 0 && !!respondDescription.trim());

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            setSearch(searchInput.trim());
            setCurrentPage(1);
        }, 350);
        return () => window.clearTimeout(timeout);
    }, [searchInput]);

    useEffect(() => { setCurrentPage(1); }, [offsetMinutes]);

    useEffect(() => {
        loadAppeals();
        return () => { requestVersion.current++; };
    }, [currentPage, pageSize, statusFilter, dateFrom, dateTo, search, offsetMinutes]);

    const handleLogout = () => {
        traderAuthAPI.logout();
        navigate('/');
    };

    const loadAppeals = async () => {
        const version = ++requestVersion.current;
        try {
            setIsLoading(true);
            setError(null);

            const filters = {
                status: statusFilter !== 'all' ? statusFilter : undefined,
                start_date: dateFrom || undefined,
                end_date: dateTo || undefined,
                search: search || undefined,
            };

            const data = await paymentsAPI.getTraderAppeals(currentPage, pageSize, filters);
            if (version !== requestVersion.current || latestOffset.current !== offsetMinutes) return;
            setAppeals(data.items);
            setTotalPages(data.pages);
            setTotalItems(data.total);
        } catch (err: any) {
            if (version !== requestVersion.current || latestOffset.current !== offsetMinutes) return;
            setAppeals([]);
            setTotalItems(0);
            setTotalPages(1);
            setError('Ошибка при загрузке апелляций');
        } finally {
            if (version === requestVersion.current) setIsLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleViewClick = async (appeal: Appeal) => {
        setSelectedAppeal(appeal);
        setSelectedTransaction(null);
        setReceiptUrl(null);
        setIsModalOpen(true);

        if (appeal.transaction_id) {
            try {
                const transactionData = await paymentsAPI.getPaymentStatus(appeal.transaction_id);
                setSelectedTransaction(transactionData);
                setReceiptUrl(paymentsAPI.getReceipt(appeal.transaction_id));
            } catch (error) {
                console.error("Ошибка при получении данных транзакции:", error);
            }
        }

    };

    const handleRespondClick = (appeal: Appeal, e?: React.MouseEvent) => {
        e?.stopPropagation();
        setSelectedAppeal(appeal);
        setRespondDescription('');
        setRespondFiles([]);
        setOtherResponseOpen(false);
        setResponseMode('evidence');
        setRequestedAmount('');
        setRecalculationDescription('');
        setRecalculationFiles([]);
        setIsRespondModalOpen(true);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const next = [...activeFiles, ...Array.from(e.target.files)];
            if (next.length > 10) {
                alert('Можно прикрепить не более 10 файлов');
            } else {
                const oversized = next.find((file) => file.size > 10 * 1024 * 1024);
                if (oversized) {
                    alert(`Файл «${oversized.name}» превышает лимит 10 МБ`);
                } else {
                    setActiveFiles(next);
                }
            }
            e.target.value = '';
        }
    };

    const handleSubmitResponse = async (reason?: TraderAppealReason) => {
        if (submittingResponse.current || !selectedAppeal
            || (reason ? !quickResponseAvailable : !responseReady)) {
            return;
        }

        submittingResponse.current = true;
        setIsSubmitting(true);
        try {
            if (reason) {
                await paymentsAPI.respondToAppeal(selectedAppeal.id, [], '', reason);
            } else if (isRecalculation) {
                await paymentsAPI.respondToAppeal(selectedAppeal.id, recalculationFiles, recalculationDescription, undefined, normalizedAmount);
            } else {
                await paymentsAPI.respondToAppeal(selectedAppeal.id, respondFiles, respondDescription);
            }
            setIsRespondModalOpen(false);
            setIsModalOpen(false);
            loadAppeals();
            alert('Ответ сохранён. Апелляция передана на рассмотрение саппорту.');
        } catch (err: any) {
            console.error('Error responding to appeal:', err);
            alert('Ошибка при отправке ответа: ' + (err.response?.data?.detail || err.message));
        } finally {
            submittingResponse.current = false;
            setIsSubmitting(false);
        }
    };

    const handleAcceptAppeal = async (appeal: Appeal, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!window.confirm('Вы уверены, что хотите согласиться с апелляцией? Спор будет закрыт в пользу мерчанта.')) return;

        try {
            await paymentsAPI.acceptAppeal(appeal.id);
            loadAppeals();
            alert('Апелляция принята. Спор закрыт в пользу мерчанта.');
        } catch (err: any) {
            console.error('Error accepting appeal:', err);
            alert('Ошибка при принятии апелляции: ' + (err.response?.data?.detail || err.message));
        }
    };

    const canRespond = (appeal: Appeal) => {
        return appeal.status === AppealStatus.OPEN && !appeal.trader_responded_at;
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case AppealStatus.OPEN:
                return <Badge className="bg-yellow-500">Ожидает вашего ответа</Badge>;
            case AppealStatus.PENDING_SUPPORT:
                return <Badge className="bg-purple-500">На рассмотрении саппорта</Badge>;
            case AppealStatus.IN_PROGRESS:
                return <Badge className="bg-indigo-500">В работе у саппорта</Badge>;
            case AppealStatus.RESOLVED:
                return <Badge className="bg-green-500">Решен</Badge>;
            case AppealStatus.REJECTED:
                return <Badge className="bg-red-500">Отклонен</Badge>;
            case AppealStatus.CANCELLED:
                return <Badge className="bg-gray-500">Отменен</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };


    return (
        <div className="flex min-h-screen bg-gray-50 dark:bg-slate-900">
            {menuOpen && (
                <button className="fixed inset-0 z-30 bg-black/40 xl:hidden" aria-label="Закрыть меню" onClick={() => setMenuOpen(false)} />
            )}
            <div id="trader-navigation" className={`${menuOpen ? 'block' : 'hidden'} fixed inset-y-0 left-0 z-40 overflow-y-auto xl:static xl:block xl:shrink-0`} onClick={event => {
                if ((event.target as HTMLElement).closest('a')) setMenuOpen(false);
            }}>
                <Sidebar />
            </div>
            <div className="min-w-0 flex-1 flex flex-col">
                {/* Header с кнопкой выхода */}
                <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 px-4 sm:px-8 py-4">
                    <div className="flex flex-wrap items-center justify-between gap-2 xl:justify-end">
                        <Button variant="outline" className="xl:hidden" aria-label={menuOpen ? 'Закрыть меню' : 'Открыть меню'} aria-expanded={menuOpen} aria-controls="trader-navigation" onClick={() => setMenuOpen(open => !open)}>
                            {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                        </Button>
                        <div className="flex items-center gap-2">
                            <Button
                                onClick={handleLogout}
                                variant="outline"
                                className="flex items-center gap-2 text-gray-600 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-300 hover:border-red-300 dark:hover:border-red-800"
                            >
                                <LogOut className="h-4 w-4" />
                                Выйти
                            </Button>
                            <ThemeToggle />
                        </div>
                    </div>
                </div>

                {/* Основной контент */}
                <div className="min-w-0 flex-1 p-4 sm:p-8 space-y-6 break-words">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                            <h2 className="text-3xl font-bold text-gray-900 dark:text-slate-100">Мои апелляции</h2>
                            <p className="text-gray-600 dark:text-slate-300 mt-2">Список спорных ситуаций по вашим ордерам · {label}</p>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={loadAppeals}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <RefreshCw className="h-4 w-4" />
                            )}
                        </Button>
                    </div>

                    {/* Фильтры */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex flex-wrap items-center gap-2">
                                <Calendar className="h-5 w-5" />
                                Фильтры
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="mb-4 space-y-2">
                                <Label htmlFor="appealSearch">Поиск по ID апелляции или транзакции</Label>
                                <Input
                                    id="appealSearch"
                                    type="search"
                                    value={searchInput}
                                    onChange={(event) => setSearchInput(event.target.value)}
                                    placeholder="Введите ID целиком или его часть"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="statusFilter">Статус</Label>
                                    <select
                                        id="statusFilter"
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-md"
                                    >
                                        <option value="all">Все</option>
                                        <option value="active">Активные</option>
                                        <option value={AppealStatus.OPEN}>Ожидает ответа</option>
                                        <option value={AppealStatus.PENDING_SUPPORT}>На рассмотрении саппорта</option>
                                        <option value={AppealStatus.IN_PROGRESS}>В работе</option>
                                        <option value={AppealStatus.RESOLVED}>Решен</option>
                                        <option value={AppealStatus.CANCELLED}>Отменен</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="dateFrom">Дата от</Label>
                                    <Input
                                        id="dateFrom"
                                        type="date"
                                        value={dateFrom}
                                        onChange={(e) => setDateFrom(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="dateTo">Дата до</Label>
                                    <Input
                                        id="dateTo"
                                        type="date"
                                        value={dateTo}
                                        onChange={(e) => setDateTo(e.target.value)}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {error && (
                        <div className="p-4 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md">
                            {error}
                        </div>
                    )}

                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-300" />
                            <span className="ml-2 text-gray-600 dark:text-slate-300">Загружаем апелляции...</span>
                        </div>
                    ) : appeals.length === 0 ? (
                        <Card>
                            <CardContent className="flex flex-col items-center justify-center py-12">
                                <AlertCircle className="h-16 w-16 text-gray-400 mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2">Апелляций не найдено</h3>
                                <p className="text-gray-500 dark:text-slate-400 text-center">
                                    {search ? 'По вашему запросу апелляции не найдены. Измените поиск или фильтры.' : 'Здесь будут отображаться все споры по вашим транзакциям'}
                                </p>
                            </CardContent>
                        </Card>
                    ) : (
                        <>
                            <div className="grid gap-4">
                                {appeals.map((appeal) => (
                                    <Card key={appeal.id} className="border border-gray-200 dark:border-slate-700 hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleViewClick(appeal)}>
                                        <CardContent className="p-4">
                                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                                <div className="min-w-0 space-y-1">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span className="font-bold text-lg break-all">#{appeal.id}</span>
                                                        {getStatusBadge(appeal.status)}
                                                        {appeal.transaction_type === 'withdraw' ? (
                                                            <Badge variant="outline" className="border-red-500 text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-950">Вывод</Badge>
                                                        ) : (
                                                            <Badge variant="outline" className="border-green-500 text-green-600 dark:text-green-300 bg-green-50 dark:bg-green-950">Пополнение</Badge>
                                                        )}
                                                        {/* Таймер апелляции (половина от настроек мерчанта) */}
                                                        {canRespond(appeal) && appeal.appeal_lifetime_minutes && (
                                                            <AppealTimer
                                                                createdAt={appeal.timer_reset_at || appeal.created_at}
                                                                lifetimeMinutes={Math.ceil(appeal.appeal_lifetime_minutes / 2)}
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-gray-500 dark:text-slate-400 flex flex-wrap items-center gap-4">
                                                        <span>Создан: {formatDate(appeal.created_at)}</span>
                                                        <span className="font-mono text-xs break-all">TX: {appeal.transaction_id}</span>
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-end gap-2">
                                                    <div className="font-bold text-lg break-all">
                                                        {appeal.amount} {appeal.currency}
                                                    </div>
                                                    {appeal.resolution_type && (
                                                        <div className={`text-sm font-medium ${appeal.resolution_type === AppealResolution.FAVOR_TRADER ? 'text-green-600 dark:text-green-300' : 'text-red-600 dark:text-red-300'}`}>
                                                            {appeal.resolution_type === AppealResolution.FAVOR_TRADER ? 'В вашу пользу' : 'В пользу мерчанта'}
                                                        </div>
                                                    )}
                                                    {/* Кнопка ответа*/}
                                                    {canRespond(appeal) && (
                                                        <div className="flex gap-2">
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="border-green-500 text-green-600 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-950"
                                                                onClick={(e) => handleAcceptAppeal(appeal, e)}
                                                            >
                                                                <CheckCircle className="h-4 w-4 mr-1" />
                                                                Согласен
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                className="bg-blue-600 hover:bg-blue-700"
                                                                onClick={(e) => handleRespondClick(appeal, e)}
                                                            >
                                                                <Send className="h-4 w-4 mr-1" />
                                                                Оспорить
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </>
                    )}
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        totalItems={totalItems}
                        pageSize={pageSize}
                        disabled={isLoading}
                        onPageSizeChange={(size) => {
                            setPageSize(size);
                            setCurrentPage(1);
                        }}
                    />
                </div>
            </div>

            {/* Modal for viewing appeal details */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto break-words [overflow-wrap:anywhere]">
                    <DialogHeader>
                        <DialogTitle>Детали апелляции #{selectedAppeal?.id}</DialogTitle>
                        <DialogDescription>
                            Транзакция: {selectedAppeal?.transaction_id}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
                        {/* Transaction Info */}
                        <div className="space-y-4">
                            <div className="bg-gray-50 dark:bg-slate-900 p-4 rounded-lg border">
                                <h3 className="font-semibold mb-3 flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4" />
                                    Транзакция
                                </h3>
                                {selectedTransaction ? (
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-500 dark:text-slate-400">Статус TX:</span>
                                            <span className="font-medium">{selectedTransaction.status}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500 dark:text-slate-400">Сумма:</span>
                                            <span className="font-bold">{selectedAppeal?.amount} {selectedAppeal?.currency}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500 dark:text-slate-400">Дата создания:</span>
                                            <span>{formatDate(selectedTransaction.assigned_at)}</span>
                                        </div>
                                        {receiptUrl && selectedTransaction?.receipt_filename && (
                                            <div className="pt-2">
                                                <Button variant="outline" size="sm" className="w-full" asChild>
                                                    <a href={receiptUrl} target="_blank" rel="noopener noreferrer">
                                                        <ExternalLink className="mr-2 h-4 w-4" />
                                                        Посмотреть чек
                                                    </a>
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex justify-center py-4">
                                        <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                                    </div>
                                )}
                            </div>

                            {selectedAppeal?.description && (
                                <div className="space-y-2">
                                    <h3 className="text-sm font-semibold flex items-center gap-2">
                                        <MessageSquare className="h-4 w-4" />
                                        Описание проблемы
                                    </h3>
                                    <div className="text-sm bg-gray-50 dark:bg-slate-900 p-3 rounded-md border italic text-gray-700 dark:text-slate-200">
                                        "{selectedAppeal.description}"
                                    </div>
                                </div>
                            )}

                            {(selectedAppeal?.attachments || []).length > 0 && (
                                <div className="space-y-2">
                                    <h3 className="text-sm font-semibold flex items-center gap-2">
                                        <Upload className="h-4 w-4" />
                                        Все материалы апелляции
                                    </h3>
                                    {(selectedAppeal?.attachments || []).map((attachment) => (
                                        <div key={attachment.id} className="flex flex-wrap items-center justify-between gap-2 rounded border bg-gray-50 dark:bg-slate-900 p-2 text-sm">
                                            <div className="min-w-0">
                                                <div className="truncate font-medium">{attachment.original_filename || 'Файл'}</div>
                                                <div className="text-xs text-gray-500 dark:text-slate-400">{formatDate(attachment.created_at)}</div>
                                            </div>
                                            <Button variant="outline" size="sm" asChild>
                                                <a href={`${API_BASE_URL}/${attachment.file_path.replace(/^\/+/, '')}`} target="_blank" rel="noopener noreferrer">
                                                    <ExternalLink className="h-4 w-4" />
                                                </a>
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Реквизиты платежа */}
                            {((selectedAppeal?.trader_requisites && Object.keys(selectedAppeal.trader_requisites).length > 0) ||
                                (selectedAppeal?.user_requisites && Object.keys(selectedAppeal.user_requisites).length > 0)) && (
                                    <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                                        <h3 className="font-semibold mb-4 flex items-center gap-2 text-blue-800 dark:text-blue-200">
                                            <CreditCard className="h-4 w-4" />
                                            Реквизиты платежа
                                        </h3>

                                        {/* Реквизиты трейдера (для deposit) */}
                                        {selectedAppeal?.trader_requisites && Object.keys(selectedAppeal.trader_requisites).length > 0 && (
                                            <div className="space-y-2 text-sm mb-4">
                                                <h4 className="text-xs font-semibold uppercase text-blue-600 dark:text-blue-300 mb-2">
                                                    Реквизиты трейдера
                                                </h4>
                                                {selectedAppeal.trader_requisites.recipient_bank && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-600 dark:text-slate-300 flex items-center gap-1">
                                                            <Building className="h-3 w-3" /> Банк:
                                                        </span>
                                                        <span className="font-medium">{bankDisplayName(selectedAppeal.trader_requisites.recipient_bank)}</span>
                                                    </div>
                                                )}
                                                {selectedAppeal.trader_requisites.recipient_name && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-600 dark:text-slate-300 flex items-center gap-1">
                                                            <User className="h-3 w-3" /> Получатель:
                                                        </span>
                                                        <span className="font-medium">{selectedAppeal.trader_requisites.recipient_name}</span>
                                                    </div>
                                                )}
                                                {selectedAppeal.trader_requisites.phone_number && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-600 dark:text-slate-300 flex items-center gap-1">
                                                            <Phone className="h-3 w-3" /> Телефон:
                                                        </span>
                                                        <span className="font-medium font-mono">{selectedAppeal.trader_requisites.phone_number}</span>
                                                    </div>
                                                )}
                                                {selectedAppeal.trader_requisites.transfer_amount && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-600 dark:text-slate-300">Сумма перевода:</span>
                                                        <span className="font-bold text-blue-700 dark:text-blue-300">{selectedAppeal.trader_requisites.transfer_amount} RUB</span>
                                                    </div>
                                                )}
                                                {selectedAppeal.trader_requisites.notes && (
                                                    <div className="pt-2 border-t">
                                                        <span className="text-gray-600 dark:text-slate-300 flex items-center gap-1 mb-1">
                                                            <MessageSquare className="h-3 w-3" /> Комментарий:
                                                        </span>
                                                        <div className="text-xs bg-white dark:bg-slate-800 p-2 rounded border text-gray-700 dark:text-slate-200 italic">
                                                            "{selectedAppeal.trader_requisites.notes}"
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Реквизиты клиента (для withdraw) */}
                                        {selectedAppeal?.user_requisites && Object.keys(selectedAppeal.user_requisites).length > 0 && (
                                            <div className="space-y-2 text-sm">
                                                <h4 className="text-xs font-semibold uppercase text-green-600 dark:text-green-300 mb-2">
                                                    Реквизиты клиента (куда переведены средства)
                                                </h4>
                                                {selectedAppeal.user_requisites.recipient_bank && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-600 dark:text-slate-300 flex items-center gap-1">
                                                            <Building className="h-3 w-3" /> Банк:
                                                        </span>
                                                        <span className="font-medium">{bankDisplayName(selectedAppeal.user_requisites.recipient_bank)}</span>
                                                    </div>
                                                )}
                                                {selectedAppeal.user_requisites.recipient_name && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-600 dark:text-slate-300 flex items-center gap-1">
                                                            <User className="h-3 w-3" /> Получатель:
                                                        </span>
                                                        <span className="font-medium">{selectedAppeal.user_requisites.recipient_name}</span>
                                                    </div>
                                                )}
                                                {selectedAppeal.user_requisites.phone_number && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-600 dark:text-slate-300 flex items-center gap-1">
                                                            <Phone className="h-3 w-3" /> Телефон:
                                                        </span>
                                                        <span className="font-medium font-mono">{selectedAppeal.user_requisites.phone_number}</span>
                                                    </div>
                                                )}
                                                {selectedAppeal.user_requisites.recipient_card && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-600 dark:text-slate-300 flex items-center gap-1">
                                                            <CreditCard className="h-3 w-3" /> Карта:
                                                        </span>
                                                        <span className="font-medium font-mono">{formatCardNumber(selectedAppeal.user_requisites.recipient_card)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                        </div>

                        {/* Resolution Info */}
                        <div className="space-y-4">
                            {selectedAppeal && (selectedAppeal.trader_response_description || selectedAppeal.trader_requested_amount != null) && (
                                <div className="p-4 rounded-lg border space-y-2">
                                    <h3 className="font-semibold">Ответ трейдера</h3>
                                    <TraderAppealResponse response={selectedAppeal} currency={selectedAppeal.currency} />
                                </div>
                            )}
                            <div className={`p-4 rounded-lg border ${selectedAppeal?.status === AppealStatus.RESOLVED ? (selectedAppeal?.resolution_type === AppealResolution.FAVOR_TRADER ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800') : 'bg-gray-50 dark:bg-slate-900'}`}>
                                <h3 className="font-semibold mb-3 flex items-center gap-2">
                                    <Clock className="h-4 w-4" />
                                    Статус решения
                                </h3>
                                <div className="space-y-3 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500 dark:text-slate-400">Текущий статус:</span>
                                        {selectedAppeal && getStatusBadge(selectedAppeal.status)}
                                    </div>

                                    {selectedAppeal?.status === AppealStatus.RESOLVED && (
                                        <>
                                            <div className="flex justify-between border-t pt-2">
                                                <span className="text-gray-500 dark:text-slate-400">Вердикт:</span>
                                                <span className={`font-bold ${selectedAppeal.resolution_type === AppealResolution.FAVOR_TRADER ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                                                    {selectedAppeal.resolution_type === AppealResolution.FAVOR_TRADER ? 'В пользу Трейдера' : 'В пользу Мерчанта'}
                                                </span>
                                            </div>
                                            <div className="min-w-0 space-y-1">
                                                <span className="text-gray-500 dark:text-slate-400">Причина решения:</span>
                                                <div className="p-2 bg-white/50 dark:bg-slate-800/50 rounded border text-gray-800 dark:text-slate-100">
                                                    {selectedAppeal.resolution_reason || 'Причина не указана'}
                                                </div>
                                            </div>
                                            <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400 pt-2">
                                                <span>Решено: {formatDate(selectedAppeal.resolved_at)}</span>
                                                <span className="flex items-center gap-1"><User className="h-3 w-3" /> {selectedAppeal.resolved_by}</span>
                                            </div>
                                        </>
                                    )}

                                    {selectedAppeal?.status === AppealStatus.IN_PROGRESS && (
                                        <div className="text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950 p-3 rounded border border-indigo-100 dark:border-indigo-800 text-center">
                                            Спор находится в обработке у оператора: <strong>{selectedAppeal.assigned_to}</strong>
                                        </div>
                                    )}

                                    {selectedAppeal?.status === AppealStatus.PENDING_SUPPORT && (
                                        <div className="text-purple-600 dark:text-purple-300 bg-purple-50 dark:bg-purple-950 p-3 rounded border border-purple-100 dark:border-purple-800 text-center">
                                            Ваш ответ сохранён. Ожидает рассмотрения саппортом.
                                        </div>
                                    )}

                                    {selectedAppeal?.status === AppealStatus.OPEN && (
                                        <div className="text-yellow-600 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-950 p-3 rounded border border-yellow-100 dark:border-yellow-800 text-center">
                                            Ожидает вашего ответа. Согласитесь или оспорьте апелляцию.
                                        </div>
                                    )}
                                </div>
                            </div>

                            {selectedAppeal?.status === AppealStatus.RESOLVED && selectedAppeal.resolution_type === AppealResolution.FAVOR_MERCHANT && (
                                <div className="p-3 bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded-md text-sm text-orange-800 dark:text-orange-200 flex items-start gap-2">
                                    <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-semibold mb-1">Информация о списании</p>
                                        <p>Так как решение принято в пользу мерчанта, соответствующая сумма была списана с вашего баланса или страховки.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsModalOpen(false)}>
                            Закрыть
                        </Button>
                        {selectedAppeal && canRespond(selectedAppeal) && (
                            <>
                                <Button
                                    variant="outline"
                                    className="border-green-500 text-green-600 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-950"
                                    onClick={(e) => {
                                        setIsModalOpen(false);
                                        handleAcceptAppeal(selectedAppeal, e);
                                    }}
                                >
                                    <CheckCircle className="h-4 w-4 mr-1" />
                                    Согласен
                                </Button>
                                <Button
                                    className="bg-blue-600 hover:bg-blue-700"
                                    onClick={() => {
                                        setIsModalOpen(false);
                                        handleRespondClick(selectedAppeal);
                                    }}
                                >
                                    <Send className="h-4 w-4 mr-1" />
                                    Оспорить
                                </Button>
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal for responding to appeal */}
            <Dialog open={isRespondModalOpen} onOpenChange={(open) => { if (!submittingResponse.current) setIsRespondModalOpen(open); }}>
                <DialogContent className="max-w-lg max-h-[90dvh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex flex-wrap items-center gap-2">
                            <Send className="h-5 w-5 text-blue-600 dark:text-blue-300" />
                            Ответ на апелляцию #{selectedAppeal?.id}
                        </DialogTitle>
                        <DialogDescription>
                            Ответьте на апелляцию. После отправки она будет передана на рассмотрение саппорту.
                        </DialogDescription>
                    </DialogHeader>

                    <fieldset className="space-y-4 py-4" disabled={isSubmitting}>
                        {quickResponseAvailable && (
                            <div className="space-y-2">
                                <p className="font-medium text-sm">Быстрый ответ</p>
                                <p className="text-xs text-gray-500 dark:text-slate-400">
                                    Нажатие отправит быстрое сообщение и передаст апелляцию саппорту. Файл не требуется.
                                </p>
                                {QUICK_REASONS.map(({ code, label }) => (
                                    <Button key={code} type="button" variant="outline" className="w-full h-auto whitespace-normal text-left justify-start"
                                        disabled={isSubmitting} onClick={() => handleSubmitResponse(code)}>
                                        {label}
                                    </Button>
                                ))}
                                <Button type="button" variant="outline" className="w-full justify-start"
                                    disabled={isSubmitting} aria-expanded={otherResponseOpen} aria-controls="other-appeal-response"
                                    onClick={() => setOtherResponseOpen(!otherResponseOpen)}>
                                    Другое
                                </Button>
                            </div>
                        )}
                        {/* Информация о таймере */}
                        {selectedAppeal?.appeal_lifetime_minutes && (
                            <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                                <Timer className="h-5 w-5 text-blue-600 dark:text-blue-300" />
                                <div className="text-sm">
                                    <span className="font-medium text-blue-800 dark:text-blue-200">Оставшееся время: </span>
                                    <AppealTimer
                                        createdAt={selectedAppeal.timer_reset_at || selectedAppeal.created_at}
                                        lifetimeMinutes={Math.ceil(selectedAppeal.appeal_lifetime_minutes / 2)}
                                    />
                                </div>
                            </div>
                        )}

                        {showResponseForm && <div id="other-appeal-response" className="space-y-4">
                            {quickResponseAvailable && (
                                <fieldset className="flex flex-wrap gap-4">
                                    <legend className="font-medium text-sm mb-2">Вариант ответа</legend>
                                    <label className="flex items-center gap-2 text-sm">
                                        <input type="radio" name="trader-response-mode" value="evidence" checked={!isRecalculation}
                                            onChange={() => setResponseMode('evidence')} />
                                        Ответ с доказательствами
                                    </label>
                                    <label className="flex items-center gap-2 text-sm">
                                        <input type="radio" name="trader-response-mode" value="recalculation" checked={isRecalculation}
                                            onChange={() => setResponseMode('recalculation')} />
                                        Перерасчёт
                                    </label>
                                </fieldset>
                            )}
                            {isRecalculation && (
                                <div className="space-y-2">
                                    <p className="text-sm">Текущая сумма: {selectedAppeal?.amount.toLocaleString('ru-RU', { maximumFractionDigits: 6 })} {selectedAppeal?.currency}</p>
                                    <Label htmlFor="requestedAmount">Новая сумма, {selectedAppeal?.currency} <span className="text-red-500">*</span></Label>
                                    <Input id="requestedAmount" inputMode="decimal" value={requestedAmount}
                                        onChange={e => setRequestedAmount(e.target.value)} aria-describedby="requested-amount-help" />
                                    <p id="requested-amount-help" className="text-xs text-gray-500 dark:text-slate-400">
                                        Сумма будет передана саппорту на рассмотрение. Укажите положительную сумму, отличную от текущей, до 6 знаков после запятой.
                                    </p>
                                </div>
                            )}
                            {/* Описание */}
                            <div className="space-y-2">
                                <Label htmlFor="responseDescription">
                                    {isRecalculation ? 'Комментарий (необязательно)' : <>Причина отклонения апелляции <span className="text-red-500">*</span></>}
                                </Label>
                                <Textarea
                                    id="responseDescription"
                                    placeholder={isRecalculation ? 'Поясните причину перерасчёта...' : 'Укажите причину, почему вы отклоняете апелляцию...'}
                                    value={activeDescription}
                                    onChange={(e) => setActiveDescription(e.target.value)}
                                    rows={4}
                                    className="resize-none"
                                />
                            </div>

                            {/* Загрузка файла */}
                            <div className="space-y-2">
                                <Label htmlFor="responseFile">
                                    {isRecalculation ? 'Файлы доказательств (необязательно)' : <>Файл доказательства <span className="text-red-500">*</span></>}
                                </Label>
                                <div className="flex flex-wrap items-center gap-2">
                                    <Input
                                        id="responseFile"
                                        type="file"
                                        multiple
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={handleFileChange}
                                        className="flex-1"
                                    />
                                </div>
                                {activeFiles.map((file, index) => (
                                    <div key={`${file.name}-${file.lastModified}-${index}`} className="flex flex-wrap items-center justify-between gap-2 rounded border p-2 text-sm">
                                        <span className="truncate">{file.name}</span>
                                        <Button type="button" variant="ghost" size="sm" onClick={() => setActiveFiles(activeFiles.filter((_, i) => i !== index))}>
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ))}
                                <p className="text-xs text-gray-500 dark:text-slate-400">
                                    До 10 файлов PDF/JPG/PNG, каждый до 10 МБ
                                </p>
                            </div>

                            {/* Предупреждение */}
                            {!isRecalculation && <div className="p-3 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                <div className="flex items-start gap-2">
                                    <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-300 mt-0.5" />
                                    <div className="text-sm text-yellow-800 dark:text-yellow-200">
                                        <p className="font-medium">Важно!</p>
                                        <p>После отправки ответа апелляция будет передана на рассмотрение саппорту. Убедитесь, что предоставили достаточные доказательства.</p>
                                    </div>
                                </div>
                            </div>}
                        </div>}
                    </fieldset>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsRespondModalOpen(false)}
                            disabled={isSubmitting}
                        >
                            Отмена
                        </Button>
                        {showResponseForm && <Button
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={() => handleSubmitResponse()}
                            disabled={isSubmitting || !responseReady}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Отправка...
                                </>
                            ) : (
                                <>
                                    <Send className="h-4 w-4 mr-2" />
                                    {isRecalculation ? 'Запросить перерасчёт' : 'Отправить ответ'}
                                </>
                            )}
                        </Button>}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
